/**
 * useNewShell — web-side counterpart to the iOS `orbit.use_new_shell`
 * AppStorage flag (#307). Reads the same localStorage key so a
 * studio that toggles the redesign on for iOS also gets it on web
 * (and vice versa) without a separate setting.
 *
 * Returns `[isOn, setIsOn]` so any component can both read AND
 * toggle the shell without prop-drilling. The setter writes to
 * localStorage and emits a `storage` event manually so other tabs +
 * other subscribers in the same tab re-render in real time.
 *
 * Used by the Web Pass 1 redesign (#319) to A/B between the
 * existing cream-light shell and the new dark Oura DNA without
 * disrupting studios on the current chrome.
 */
import { useCallback, useEffect, useState } from 'react';

const STORAGE_KEY = 'orbit.use_new_shell';

/// Read the current flag synchronously so the first render paints
/// the right shell (no flash). Guards against SSR / non-browser
/// contexts even though the app is client-only today.
function read(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    return window.localStorage.getItem(STORAGE_KEY) === '1';
  } catch {
    return false;
  }
}

export function useNewShell(): [boolean, (next: boolean) => void] {
  const [value, setValue] = useState<boolean>(read);

  useEffect(() => {
    // Cross-tab + cross-subscriber sync — the `storage` event fires
    // on OTHER tabs natively. We also dispatch it from set() so
    // subscribers in the SAME tab pick up changes without a refresh.
    const handler = (e: StorageEvent) => {
      if (e.key !== STORAGE_KEY) return;
      setValue(e.newValue === '1');
    };
    window.addEventListener('storage', handler);
    return () => window.removeEventListener('storage', handler);
  }, []);

  const set = useCallback((next: boolean) => {
    try {
      window.localStorage.setItem(STORAGE_KEY, next ? '1' : '0');
      // Same-tab notification — the native `storage` event only
      // fires across tabs. We dispatch a synthetic one so any
      // useNewShell() callers in this tab also re-render.
      window.dispatchEvent(
        new StorageEvent('storage', {
          key: STORAGE_KEY,
          newValue: next ? '1' : '0',
        }),
      );
    } catch {
      // Private mode / quota error — set in-memory state so the
      // toggle still works for the session even if it can't persist.
    }
    setValue(next);
  }, []);

  return [value, set];
}
